import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { UserCog, UserPlus, Trash2, Shield, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffManagement() {
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingStaff, setDeletingStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    // Check if user is admin
    const role = localStorage.getItem('userRole');
    if (role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchStaffList();
  }, [navigate]);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/staff-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to fetch staff list');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/create-staff`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Staff user "${formData.username}" created successfully`);
      setShowAddDialog(false);
      setFormData({ username: '', email: '', password: '' });
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    }
  };

  const handleDeleteStaff = async () => {
    if (!deletingStaff) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/staff/${deletingStaff.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff user deleted successfully');
      setShowDeleteDialog(false);
      setDeletingStaff(null);
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="font-inter text-concrete-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight flex items-center gap-3">
            <UserCog className="h-10 w-10 text-brick-600" />
            Staff Management
          </h1>
          <p className="font-inter text-concrete-600 mt-2">Manage staff user accounts</p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-brick-600 hover:bg-brick-500 text-white shadow-md font-manrope font-semibold"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Add Staff
        </Button>
      </div>

      <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
        <div className="p-6 border-b border-concrete-200">
          <h2 className="font-manrope font-bold text-xl text-concrete-900">Staff Users</h2>
          <p className="font-inter text-sm text-concrete-600 mt-1">
            Staff users can access all data but cannot reset or restore data
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-concrete-50 border-b border-concrete-200">
              <tr>
                <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Username</th>
                <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Email</th>
                <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Role</th>
                <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Created</th>
                <th className="px-6 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-concrete-100">
              {staffList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center font-inter text-concrete-500">
                    No staff users added yet
                  </td>
                </tr>
              ) : (
                staffList.map((staff) => (
                  <tr key={staff.id} className="hover:bg-concrete-50">
                    <td className="px-6 py-4 font-inter font-medium text-concrete-900">{staff.username}</td>
                    <td className="px-6 py-4 font-inter text-concrete-700">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-concrete-400" />
                        {staff.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <Shield className="h-3 w-3" />
                        Staff
                      </span>
                    </td>
                    <td className="px-6 py-4 font-inter text-concrete-600 text-sm">
                      {new Date(staff.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        <Button
                          onClick={() => {
                            setDeletingStaff(staff);
                            setShowDeleteDialog(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-brick-600" />
              Add New Staff User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-2"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2"
                placeholder="Enter email address"
                required
              />
            </div>
            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2"
                placeholder="Enter password"
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-brick-600 hover:bg-brick-500">
                <UserPlus className="mr-2 h-4 w-4" />
                Create Staff
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold text-red-900">
              Delete Staff User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingStaff?.username}</strong>? 
              This user will no longer be able to access the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingStaff(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteStaff} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
